/**
 * In-game house-ad banner slot for the play-chrome top bar (sc-6959).
 *
 * Thin shim over the provider-agnostic ad manager (../ads/manager.ts). The
 * slot lives in shell chrome, never inside the game iframe — the game has no
 * idea ads exist. The manager owns creative selection, the paying-user gate
 * (entitled players get NO banner), and provider fallback; this file only
 * adapts the manager's BannerHandle to the AdSlot shape play.ts already ticks.
 *
 * Scope is unchanged: stray-pixel only (enforced by AD_CONFIG.bannerCartridges
 * in the manager). Every other cartridge gets NULL_SLOT. The tick runs off
 * requestAnimationFrame (throttled in hidden tabs), so rotation only advances
 * on visible time.
 *
 * The house creatives + weighted rotation now live in ../ads/house-provider.ts;
 * HOUSE_ADS/pickAd are re-exported here for back-compat with existing imports.
 */

import { getAdManager } from '../ads/manager.js';

export { HOUSE_ADS, pickAd, type HouseAd } from '../ads/house-provider.js';

export interface AdSlot {
  /** rAF-friendly tick; pass the frame delta in ms. */
  tick(deltaMs: number): void;
  /** Tear down: stop rotation, hide, drop listeners. */
  dispose(): void;
}

/** No-op slot for cartridges without ads (keeps play.ts branch-free). */
const NULL_SLOT: AdSlot = { tick: () => {}, dispose: () => {} };

/**
 * Mount the ad slot into `chrome` for `cartridgeId`. Returns a live slot the
 * caller ticks each visible frame; a non-ad cartridge OR an entitled player
 * gets NULL_SLOT (the manager decides).
 */
export function mountAdSlot(chrome: HTMLElement, cartridgeId: string): AdSlot {
  const handle = getAdManager().mountBanner(chrome, 'play-chrome', cartridgeId);
  return handle ?? NULL_SLOT;
}
